import { useEffect, useState, useCallback, useRef } from 'react';
import API from '../api';
import { useAuth } from '../context/AuthContext';

// ── 색상 코드 (재고관리와 동일) — group_id에 재고관리 옵션(stock_columns)이 하나도
// 등록되어 있지 않은 예전 데이터를 위한 최후의 폴백으로만 사용
const COLORS      = ['BK','BL','RD','OG','GF','YE'];
const COLOR_LABEL = { BK:'검정', BL:'파랑', RD:'빨강', OG:'주황', GF:'형광', YE:'노랑' };
const COLOR_STYLE = {
  BK:{ bg:'#222',    color:'#fff' },
  BL:{ bg:'#1d4ed8', color:'#fff' },
  RD:{ bg:'#dc2626', color:'#fff' },
  OG:{ bg:'#ea580c', color:'#fff' },
  GF:{ bg:'#84cc16', color:'#fff' },
  YE:{ bg:'#facc15', color:'#222' },
};
const FALLBACK_STOCK_OPTIONS = COLORS.map(c => ({ name: c, bg_color: COLOR_STYLE[c].bg, text_color: COLOR_STYLE[c].color }));

// 생산전광판 하위 제품군 선택 상태 — 사이드바(App.js)와 동일한 localStorage 키/이벤트를 사용한다.
// 사이드바에서 체크박스/제품군 이름을 눌러 바뀐 선택값을 같은 탭 안에서 바로 반영해야 하므로
// (storage 이벤트는 다른 탭에서만 발생함) 커스텀 이벤트('display:groups-changed')로 전달받는다.
const DISPLAY_GROUPS_KEY = 'ims_display_group_ids';
const readSelectedGroupIds = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(DISPLAY_GROUPS_KEY) || '[]');
    return Array.isArray(raw) ? raw : [];
  } catch { return []; }
};

// 생산현황 상태 → 짧은 라벨/색상
const STATUS_SHORT = { '생산진행': '생산', '생산완료': '완료', '생산보류': '보류' };
const STATUS_COLOR = { '생산진행': '#3b82f6', '생산완료': '#22c55e', '생산보류': '#f43f5e' };

const formatNum = n => (n || 0).toLocaleString();

// '2026. 07. 28. 19:17:54' 또는 '2026-07-28' 등 어떤 형식이든 'YYYY-MM-DD'로 통일
const toYMD = (str) => {
  if (!str) return null;
  const m = String(str).match(/(\d{4})\D+(\d{1,2})\D+(\d{1,2})/);
  if (!m) return null;
  const pad = n => String(n).padStart(2, '0');
  return `${m[1]}-${pad(m[2])}-${pad(m[3])}`;
};

// D-Day 계산
const calcDday = (dateStr) => {
  if (!dateStr) return null;
  const today  = new Date(); today.setHours(0,0,0,0);
  const target = new Date(dateStr); target.setHours(0,0,0,0);
  return Math.ceil((target - today) / (1000*60*60*24));
};

function DdayBadge({ endDate, completedAt }) {
  if (completedAt) {
    return <span style={{ fontSize:17, padding:'4px 14px', borderRadius:24, background:'#22c55e22', color:'#22c55e', fontWeight:700, whiteSpace:'nowrap' }}>완료</span>;
  }
  const d = calcDday(endDate);
  if (d === null) return <span style={{ color:'#64748b', fontSize:15 }}>-</span>;
  const color = d<0?'#f87171':d<=3?'#fbbf24':'#3b82f6';
  const label = d<0?`D+${Math.abs(d)}`:d===0?'D-Day':`D-${d}`;
  return <span style={{ fontSize:17, padding:'4px 14px', borderRadius:24, background:color+'22', color, fontWeight:700, whiteSpace:'nowrap' }}>{label}</span>;
}

// 생산현황 한 줄 항목 (모델명 + 진행바)
function ProductionRow({ p }) {
  const pct  = Math.min(100, p.progress_rate || 0);
  const done = p.status === '생산완료';
  const barColor = done ? '#16a34a' : '#b45309';
  const overdue  = !done && calcDday(p.end_date) < 0;

  return (
    <div style={{ display:'flex', alignItems:'center', gap:14, padding:'14px 16px', borderBottom:'1px solid #0f172a' }}>
      <span style={{ width:46, fontSize:17, fontWeight:700, color: STATUS_COLOR[p.status], flexShrink:0 }}>
        {STATUS_SHORT[p.status] || p.status}
      </span>
      <span style={{ width:130, fontSize:18, fontWeight:700, color:'#e2e8f0', flexShrink:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
        {p.model_no}
      </span>
      <span style={{ fontSize:16, padding:'4px 12px', borderRadius:14, fontWeight:700, flexShrink:0,
        background:COLOR_STYLE[p.color]?.bg||'#334155', color:COLOR_STYLE[p.color]?.color||'#fff' }}>
        {p.color}
      </span>
      <div style={{ position:'relative', flex:1, height:34, background:'#1e293b', borderRadius:8, overflow:'hidden' }}>
        <div style={{ position:'absolute', left:0, top:0, bottom:0, width:pct+'%', background:barColor }} />
        <div style={{ position:'relative', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, fontWeight:700, color:'#fff' }}>
          {formatNum(p.produced_qty)} ({pct}%)
        </div>
      </div>
      <span style={{ width:80, fontSize:17, fontWeight:600, color:'#94a3b8', textAlign:'right', flexShrink:0 }}>{formatNum(p.target_qty)}</span>
      <span style={{ width:110, fontSize:17, color: overdue?'#f87171':'#94a3b8', textAlign:'center', flexShrink:0 }}>
        {p.completed_at ? (toYMD(p.completed_at) || '완료') : (p.end_date || '-')}
      </span>
      <span style={{ width:70, flexShrink:0, textAlign:'right' }}>
        <DdayBadge endDate={p.end_date} completedAt={p.completed_at} />
      </span>
    </div>
  );
}

// 현재 시각
function Clock() {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const pad = n => String(n).padStart(2, '0');
  return (
    <div style={{ color: '#fff', fontSize: 48, fontWeight: 700, letterSpacing: 2, fontVariantNumeric:'tabular-nums' }}>
      {pad(time.getHours())}:{pad(time.getMinutes())}:{pad(time.getSeconds())}
    </div>
  );
}

// 공지 내용 2줄 표시 + 내용이 길면 5초마다 한 줄씩 위로 스크롤
function NoticeTicker({ content }) {
  const LINE_HEIGHT = 28; // px, 폰트 19px 기준
  const [offset, setOffset] = useState(0);
  const [totalLines, setTotalLines] = useState(1);
  const measureRef = useRef(null);

  useEffect(() => {
    setOffset(0);
    const el = measureRef.current;
    if (el) {
      const lines = Math.max(1, Math.round(el.scrollHeight / LINE_HEIGHT));
      setTotalLines(lines);
    }
  }, [content]);

  useEffect(() => {
    if (totalLines <= 2) return; // 2줄 이하면 스크롤 불필요
    const maxOffset = totalLines - 2;
    const timer = setInterval(() => {
      setOffset(prev => (prev >= maxOffset ? 0 : prev + 1));
    }, 5000);
    return () => clearInterval(timer);
  }, [totalLines]);

  return (
    <div style={{ position:'relative', overflow:'hidden', height: LINE_HEIGHT*2, flex:1 }}>
      <div
        ref={measureRef}
        style={{ position:'absolute', left:0, right:0, top: -offset*LINE_HEIGHT,
          transition:'top 0.6s ease', fontSize:19, lineHeight:LINE_HEIGHT+'px', color:'#94a3b8' }}>
        {content}
      </div>
    </div>
  );
}

// 완제품 재고현황 표: 기본 4줄 고정 표시. 등록된 완제품이 4개를 초과하면 5초마다
// 한 줄(스텝)씩 아래→위로 슬라이딩하며 순환 표시한다(NoticeTicker와 동일한 스텝-슬라이딩 방식).
function StockRowsTicker({ products, options, gridCols }) {
  const ROWS_VISIBLE = 4;
  const ROW_HEIGHT = 55; // px — 4줄 기준 표 전체 높이 220px(55*4)

  const [offset, setOffset] = useState(0);
  useEffect(() => { setOffset(0); }, [products.length]);
  useEffect(() => {
    if (products.length <= ROWS_VISIBLE) return; // 4줄 이하면 슬라이딩 불필요
    const maxOffset = products.length - ROWS_VISIBLE;
    const timer = setInterval(() => {
      setOffset(prev => (prev >= maxOffset ? 0 : prev + 1));
    }, 5000);
    return () => clearInterval(timer);
  }, [products.length]);

  return (
    <div style={{ position:'relative', overflow:'hidden', height: ROW_HEIGHT * ROWS_VISIBLE }}>
      <div style={{ position:'absolute', left:0, right:0, top: -offset * ROW_HEIGHT, transition:'top 0.6s ease' }}>
        {products.map((p, i) => (
          <div key={p.model_no + i} style={{ display:'grid', gridTemplateColumns: gridCols, height: ROW_HEIGHT,
            alignItems:'center', borderBottom:'1px solid #0f172a', boxSizing:'border-box' }}>
            <div style={{ padding:'0 16px 0 46px', overflow:'hidden' }}>
              <span style={{ color:'#e2e8f0', fontWeight:700, fontSize:18 }}>{p.model_no}</span>
              <span style={{ color:'#64748b', fontSize:14, marginLeft:6 }}>({p.name})</span>
            </div>
            {options.map(opt => {
              const qty = p.stocks?.[opt.name] || 0;
              const isShort = qty < 0;
              return (
                <div key={opt.name} style={{ textAlign:'center', padding:'0 10px',
                  color: isShort?'#ef4444':qty===0?'#475569':qty<50?'#fbbf24':'#22c55e', fontWeight:700, fontSize:18 }}>
                  {isShort ? '-' + formatNum(Math.abs(qty)) : formatNum(qty)}
                </div>
              );
            })}
            <div style={{ textAlign:'center', padding:'0 28px', color:'#94a3b8', fontSize:15, overflow:'hidden' }}>
              {p.updated_at ? `${p.updated_at} (${p.updated_by || '-'})` : '-'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Display() {
  const { user, logout } = useAuth();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [now, setNow]         = useState(new Date());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showCursor, setShowCursor] = useState(true);
  const cursorTimerRef = useRef(null);

  // 좌측 사이드바(재고관리에 등록된 제품군)에서 선택한 제품군 id 목록. 2개 이상 선택하면
  // 30초 폴링 주기마다 하나씩 번갈아 보여주고, 0~1개면 항상 그 제품군(또는 서버 기본값)만 보여준다.
  const [selectedGroupIds, setSelectedGroupIds] = useState(readSelectedGroupIds);
  const selectedGroupIdsRef = useRef(selectedGroupIds);
  useEffect(() => { selectedGroupIdsRef.current = selectedGroupIds; }, [selectedGroupIds]);
  const rotationIdxRef = useRef(0);

  const loadForGroup = useCallback((groupId) => {
    const params = groupId ? { group_id: groupId } : {};
    API.get('/display', { params }).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);
  const loadCurrent = useCallback(() => {
    const ids = selectedGroupIdsRef.current;
    loadForGroup(ids.length ? ids[rotationIdxRef.current % ids.length] : null);
  }, [loadForGroup]);

  // 30초마다 호출됨: 선택된 제품군이 2개 이상이면 다음 제품군으로 순환하고, 그 데이터를 불러온다.
  // 1개 이하면 로테이션 없이 같은(또는 서버 기본) 제품군 데이터를 새로고침만 한다.
  const tick = useCallback(() => {
    const ids = selectedGroupIdsRef.current;
    rotationIdxRef.current = ids.length > 1 ? (rotationIdxRef.current + 1) % ids.length : 0;
    loadForGroup(ids.length ? ids[rotationIdxRef.current] : null);
  }, [loadForGroup]);

  useEffect(() => {
    loadCurrent();
    const interval = setInterval(tick, 30000);
    const clockTick = setInterval(() => setNow(new Date()), 1000);
    return () => { clearInterval(interval); clearInterval(clockTick); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 이미 생산전광판 화면을 보고 있는 상태에서 사이드바의 "생산전광판" 메뉴를 다시 클릭하면
  // (같은 경로라 화면이 새로 마운트되지 않아 위 useEffect가 다시 실행되지 않음) 사이드바가
  // 쏴주는 커스텀 이벤트를 받아서 클릭 시점 기준으로 즉시 최신 데이터를 다시 가져옴(로테이션 유지)
  useEffect(() => {
    window.addEventListener('display:refresh', loadCurrent);
    return () => window.removeEventListener('display:refresh', loadCurrent);
  }, [loadCurrent]);

  // 사이드바에서 제품군 선택(체크박스/이름 클릭)이 바뀌면 즉시 반영하고, 로테이션은
  // 첫 번째로 선택된 제품군부터 다시 시작한다.
  useEffect(() => {
    const handler = () => {
      const ids = readSelectedGroupIds();
      setSelectedGroupIds(ids);
      selectedGroupIdsRef.current = ids;
      rotationIdxRef.current = 0;
      loadForGroup(ids.length ? ids[0] : null);
    };
    window.addEventListener('display:groups-changed', handler);
    return () => window.removeEventListener('display:groups-changed', handler);
  }, [loadForGroup]);

  // 사이드바에서 제품군 "이름"을 클릭하면(체크박스와는 별개의 동작) 그 제품군 하나만 지금
  // 즉시 보여준다. 체크박스 선택(로테이션 대상)이나 로테이션 순번은 전혀 건드리지 않으므로
  // 다음 30초 주기가 되면 다시 체크박스 선택 기준으로 자연스럽게 돌아간다.
  useEffect(() => {
    const handler = (e) => {
      const groupId = e?.detail?.groupId;
      if (groupId) loadForGroup(groupId);
    };
    window.addEventListener('display:view-group', handler);
    return () => window.removeEventListener('display:view-group', handler);
  }, [loadForGroup]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      const el = document.getElementById('display-content');
      (el?.requestFullscreen ? el : document.documentElement).requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // 전체화면일 때: 마우스가 3초 이상 움직이지 않으면 커서를 숨김. 움직이면 즉시 다시 보임.
  useEffect(() => {
    if (!isFullscreen) {
      setShowCursor(true);
      if (cursorTimerRef.current) clearTimeout(cursorTimerRef.current);
      return;
    }
    const resetTimer = () => {
      setShowCursor(true);
      if (cursorTimerRef.current) clearTimeout(cursorTimerRef.current);
      cursorTimerRef.current = setTimeout(() => setShowCursor(false), 3000);
    };
    resetTimer();
    window.addEventListener('mousemove', resetTimer);
    return () => {
      window.removeEventListener('mousemove', resetTimer);
      if (cursorTimerRef.current) clearTimeout(cursorTimerRef.current);
    };
  }, [isFullscreen]);

  if (loading) {
    return (
      <div id="display-content" style={{ minHeight: '100vh', background: '#0f172a', padding: 24, boxSizing: 'border-box', fontFamily: 'sans-serif' }}>
        <div style={{ color: '#fff', fontSize: 24 }}>데이터 불러오는 중...</div>
      </div>
    );
  }

  const summary   = data?.prodSummary || { targetQty:0, producedQty:0, rate:0, monthQty:0, monthLabel:'' };
  const monthNum  = summary.monthLabel ? Number(summary.monthLabel.split('-')[1]) : new Date().getMonth()+1;
  const productions = data?.productions || [];
  const products     = data?.products || [];
  const notices       = data?.notices || [];
  const weather        = data?.weather || null;
  const stockOptions   = (data?.stockOptions && data.stockOptions.length > 0) ? data.stockOptions : FALLBACK_STOCK_OPTIONS;
  const groupName       = data?.groupName || '나인세이프티';

  const jicheList = productions.filter(p => p.category === '자체생산');
  const jumunList = productions.filter(p => p.category === '주문생산');

  const days = ['일','월','화','수','목','금','토'];
  const dateStr = `${now.getFullYear()}년 ${now.getMonth()+1}월 ${now.getDate()}일(${days[now.getDay()]})`;
  const noticeMain = notices[0];

  return (
    <div id="display-content"
      style={{ minHeight: '100vh', background: '#0f172a', padding: '22px 32px', boxSizing: 'border-box', fontFamily: 'sans-serif',
        cursor: isFullscreen && !showCursor ? 'none' : 'default' }}>

      {/* 전체화면일 때 키보드 포커스(탭 이동 등) 아웃라인 숨김 */}
      <style>{`
        #display-content:fullscreen *:focus {
          outline: none !important;
          box-shadow: none !important;
        }
        #display-content:-webkit-full-screen *:focus {
          outline: none !important;
          box-shadow: none !important;
        }
      `}</style>

      {/* ── 헤더: 좌(날짜/날씨) / 중앙(타이틀) / 우(시계) ── */}
      <div style={{ position:'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 30 }}>
        <div>
          <div style={{ color:'#fff', fontSize:24, fontWeight:600 }}>{dateStr}</div>
          <div style={{ color:'#94a3b8', fontSize:19, marginTop:4 }}>
            {weather ? (
              // 날씨이슈 | 현재온도 (최고온도/최저온도) — 홈 화면과 동일한 날씨 조회 방식(Open-Meteo) 사용
              <>{weather.label && <>{weather.label}&nbsp;|&nbsp;</>}
                {weather.temp != null ? `${weather.temp}°C` : '-'}
                {' '}({weather.high != null ? `${weather.high}°` : '-'}/{weather.low != null ? `${weather.low}°` : '-'})
              </>
            ) : '날씨 정보 없음'}
          </div>
        </div>

        <div style={{ position:'absolute', left:'50%', transform:'translateX(-50%)',
          color:'#fff', fontWeight:700, fontSize:38, letterSpacing:1, whiteSpace:'nowrap' }}>
          {groupName} 생산 &amp; 재고관리 현황
        </div>

        <div style={{ display:'flex', alignItems:'center', gap:14 }}>
          {!isFullscreen && (
            <>
              <button onClick={loadCurrent}
                style={{ background:'#1e293b', color:'#94a3b8', border:'1px solid #334155', borderRadius:8,
                  padding:'8px 16px', fontSize:15, fontWeight:500, cursor:'pointer' }}>
                🔄 새로고침
              </button>
              <button onClick={toggleFullscreen}
                style={{ background:'#1e293b', color:'#94a3b8', border:'1px solid #334155', borderRadius:8,
                  padding:'8px 16px', fontSize:15, fontWeight:500, cursor:'pointer' }}>
                ⛶ 전체화면
              </button>
            </>
          )}
          <Clock />
        </div>
      </div>

      {/* ── 생산 통계 요약 카드 ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 15 }}>
        {[
          { label: `${monthNum}월 생산목표량`, count: summary.targetQty,   suffix:'',  icon: '📋', color: '#d97706', bg: '#1c1509' },
          { label: `${monthNum}월 생산수량`,   count: summary.producedQty, suffix:'',  icon: '⚙️', color: '#3b82f6', bg: '#0c1829' },
          { label: `${monthNum}월 공정률`,     count: summary.rate,        suffix:'%', icon: '✅', color: '#22c55e', bg: '#0a1f10' },
          { label: '오늘 생산량',              count: summary.monthQty,    suffix:'',  icon: '📈', color: '#8b5cf6', bg: '#150c29' },
        ].map(s => (
          <div key={s.label} style={{ background: s.bg, border: '1px solid ' + s.color + '40', borderRadius: 16, padding: '15px 26px', display: 'flex', alignItems: 'center', gap: 20 }}>
            <div style={{ fontSize: 40 }}>{s.icon}</div>
            <div>
              <div style={{ fontSize: 18, color: '#94a3b8' }}>{s.label}</div>
              <div style={{ fontSize: 44, fontWeight: 700, color: s.color, lineHeight: 1.3 }}>{formatNum(s.count)}{s.suffix}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── 생산현황 (자체생산 | 주문생산, 좌우 2단) ── */}
      <div style={{ background: '#1e293b', borderRadius: 16, padding: 22, border: '1px solid #334155', marginBottom: 15 }}>
        <div style={{ fontSize: 22, fontWeight: 600, color: '#fff', marginBottom: 16 }}>🏭 생산현황</div>

        {/* 생산 항목이 하나도 없어도 아래 2단 박스(자체생산/주문생산)를 그대로 렌더링해서
            4줄 표시일 때와 동일한 세로폭(height:250)을 유지함 — 각 칸 안에서 개별적으로 "해당 항목이 없습니다" 처리 */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
          {[['자체생산', jicheList], ['주문생산', jumunList]].map(([cat, rows]) => (
            <div key={cat}>
              <div style={{ fontSize: 18, fontWeight: 600, color: cat==='자체생산'?'#3b82f6':'#f59e0b',
                marginBottom: 10, paddingLeft: 10, borderLeft: `4px solid ${cat==='자체생산'?'#3b82f6':'#f59e0b'}` }}>
                📌 {cat}
              </div>
              <div style={{ background:'#0f172a', borderRadius:10, border:'1px solid #263449', height:250, overflow:'hidden' }}>
                {rows.length === 0 ? (
                  <div style={{ color:'#475569', fontSize:16, textAlign:'center', padding:'24px 0' }}>해당 항목이 없습니다.</div>
                ) : rows.map(p => <ProductionRow key={p.id} p={p} />)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── 재고현황 (완제품, 모델×옵션 + 업데이트 기록) ── */}
      <div style={{ background: '#1e293b', borderRadius: 16, padding: 20, border: '1px solid #334155', marginBottom: 15 }}>

        {products.length === 0 ? (
          <>
            <div style={{ fontSize: 22, fontWeight: 600, color: '#fff', marginBottom: 14 }}>📦 완제품 재고현황</div>
            <div style={{ color: '#475569', fontSize: 18, textAlign: 'center', padding: '36px 0' }}>등록된 완제품이 없습니다.</div>
          </>
        ) : (() => {
          // 첫 칸(모델명)/마지막 칸(업데이트 기록)은 고정폭, 옵션 칸들은 남는 폭을 균등하게 나눠 가짐 —
          // 헤더와 슬라이딩되는 본문 행이 같은 gridTemplateColumns를 써야 세로줄이 어긋나지 않는다.
          const gridCols = `280px repeat(${stockOptions.length}, 1fr) 420px`;
          return (
            <div style={{ overflowX: 'auto' }}>
              <div style={{ minWidth: 280 + stockOptions.length * 90 + 420 }}>
                <div style={{ display:'grid', gridTemplateColumns: gridCols, borderBottom:'1px solid #334155' }}>
                  <div style={{ padding:'12px 16px', fontSize:19, fontWeight:600, color:'#fff', whiteSpace:'nowrap',
                    display:'flex', alignItems:'center', gap:8 }}>
                    <span style={{ width:22, display:'inline-block', textAlign:'center' }}>📦</span>
                    <span>완제품 재고현황</span>
                  </div>
                  {stockOptions.map(opt => (
                    <div key={opt.name} style={{ padding:'12px 10px', fontSize:15, fontWeight:700, color: opt.text_color || '#fff', textAlign:'center',
                      background: opt.bg_color || '#334155' }}>
                      {opt.name}
                    </div>
                  ))}
                  <div style={{ padding:'12px 28px', fontSize:15, color:'#fff', fontWeight:700, textAlign:'center',
                    background:'#475569' }}>업데이트 기록</div>
                </div>

                <StockRowsTicker products={products} options={stockOptions} gridCols={gridCols} />
              </div>
            </div>
          );
        })()}
      </div>

      {/* ── 공지사항 (제목 고정 + 내용 2줄 자동 스크롤) ── */}
      <div style={{ background: '#1e293b', borderRadius: 16, padding: '18px 26px', border: '1px solid #334155', display:'flex', alignItems:'center', gap:18 }}>
        <span style={{ fontSize: 22, fontWeight: 600, color: '#fff', flexShrink:0, whiteSpace:'nowrap' }}>📢 공지사항</span>
        {noticeMain ? (
          <>
            <span style={{ fontSize:19, color:'#fbbf24', fontWeight:700, flexShrink:0, whiteSpace:'nowrap' }}>
              {noticeMain.title}
            </span>
            {noticeMain.content && (
              <>
                <span style={{ fontSize:19, color:'#64748b', flexShrink:0 }}>|</span>
                <NoticeTicker content={noticeMain.content} />
              </>
            )}
          </>
        ) : (
          <span style={{ fontSize:19, color:'#475569' }}>등록된 공지사항이 없습니다.</span>
        )}
      </div>

      {/* 생산현황판 전용 계정은 좌측 메뉴(및 그 안의 로그아웃 버튼)가 아예 보이지 않으므로,
          여기 화면 우측 하단에 로그아웃 버튼을 대신 띄워준다(일반 계정은 사이드바로 로그아웃하므로
          여기서는 보이지 않는다). 사내 메신저 아이콘(ChatSidebarMenu)도 항상 우측 하단(bottom:20)에
          떠 있어서, 그것과 겹치지 않도록 그 위쪽(bottom:100)에 둔다. */}
      {user?.accountType === 'display_only' && (
        <button onClick={() => logout()}
          style={{ position:'fixed', right:20, bottom:100, background:'#1e293b', color:'#94a3b8',
            border:'1px solid #334155', borderRadius:8, padding:'10px 18px', fontSize:14,
            fontWeight:600, cursor:'pointer', zIndex:4000 }}
          onMouseEnter={e => { e.currentTarget.style.background='#334155'; e.currentTarget.style.color='#fff'; }}
          onMouseLeave={e => { e.currentTarget.style.background='#1e293b'; e.currentTarget.style.color='#94a3b8'; }}>
          🚪 로그아웃
        </button>
      )}
    </div>
  );
}
